import * as _ from 'lodash';
import { FormEvent, useEffect, useState } from 'react';
import { connect, Dispatch } from 'react-redux';
import { IVersionedFile, IDocumentsVersion } from '@nkr/models/index';
import { OrganType, VersionedFileEntityType } from '@nkr/enums/index';
import { AppActionCreators, CenterActionCreators, IApplicationState, IAppState, ICenterLegalDocumentsResponse } from '@nkr/store/index';
import { Row, Col, ControlLabel, Form, FormGroup, Panel } from 'react-bootstrap';
import { downloadFile } from '@nkr/services/download-file';
import { IFileItem } from './file-item';
import styled from 'styled-components';
import SectionList from './section-list';
import { useAppHookContext } from '@nkr/utils/rest-api';

const FormWrapper = styled(Form)`
    .form-group {
        margin-bottom: 0px;
    }
`;

interface IProps {
    centerId: number,
    isAdmin: boolean,
    isAdminUser: boolean
    onTabClick: (href: string) => void
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getData: (centerId: number) => Promise<ICenterLegalDocumentsResponse>,
    getVersionedFile: (centerId: number, id: number) => Promise<any>
}

type TProps = IProps & IStateProps & IDispatchProps;

const CenterLegalDocuments = (props: TProps) => {
    const [streamlinedFiles, setStreamlinedFiles] = useState<IVersionedFile[]>([]);
    const [notStreamlinedFiles, setNotStreamlinedFiles] = useState<IVersionedFile[]>([]);
    const [documentVersions, setDocumentVersions] = useState<IDocumentsVersion[]>([]);
    const [isStreamlinedContract, setIsStreamlinedContract] = useState(false);
    const [invoicePoNumber, setInvoicePoNumber] = useState('');
    const [invoicePoExpirationDate, setInvoicePoExpirationDate] = useState('');

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        props.showLoading('Getting legal documents, one moment please...').then(() => {
            props.getData(props.centerId)
                .then(response => {
                    let { streamlinedFiles, notStreamlinedFiles, documentVersions, isStreamlinedContract, invoicePoNumber, invoicePoExpirationDate } = response.data.results;

                    setStreamlinedFiles(streamlinedFiles);
                    setNotStreamlinedFiles(notStreamlinedFiles);
                    setDocumentVersions(documentVersions);
                    setIsStreamlinedContract(isStreamlinedContract);
                    setInvoicePoNumber(invoicePoNumber);
                    setInvoicePoExpirationDate(invoicePoExpirationDate);
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleFileDownload = (e: FormEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        e.preventDefault();

        let id = parseInt(e.currentTarget.id);

        props.getVersionedFile(props.centerId, id).then(response => {
            downloadFile(response.data, `file.pdf`, response.headers.contentType);
        });
    };

    let list: IFileItem[] = [
        { label: 'Service Contract', entityType: VersionedFileEntityType.CenterContract },
        { label: 'Center BAA', entityType: VersionedFileEntityType.CenterBaaFile },
        { label: 'Contract Addendum', entityType: VersionedFileEntityType.CenterContractAddendum },
        { label: 'Purchase Order Document', entityType: VersionedFileEntityType.CenterPoDocument }
    ];

    let includeTypes = documentVersions.map(x => x.entityType);
    let streamlinedList = list.filter(x => includeTypes.includes(x.entityType));
    let legacyList = list.filter(x => !includeTypes.includes(x.entityType) || x.entityType == VersionedFileEntityType.CenterContract);

    let baseUrl = 'https://www.kidneyregistry.com';
    let label = 'NKR';

    if (props.app.site.organType == OrganType.Liver) {
        baseUrl = 'https://www.liverregistry.com';
        label = 'NLR';
    }

    let termsAndConditionsLink = props.app.site.organType == OrganType.Liver
        ? 'https://www.liverregistry.com/terms'
        : isStreamlinedContract ? `${baseUrl}/membership-terms` : `https://www.kidneyregistry.com/membership-terms-legacy`;

    const isLiver = props.app.site.organType == OrganType.Liver;

    let linkList = [
        { label: `${label} Member Center Terms & Conditions`, href: termsAndConditionsLink },
        ...(!isLiver ? [
            { label: 'Medical Board Policies', href: `${baseUrl}/for-centers/medical-board-policies` },
            { label: 'Member Center Requirements: Routine', href: `${baseUrl}/for-centers/member-center-requirements-routine` },
            { label: 'Member Center Requirements: General', href: `${baseUrl}/for-centers/member-center-requirements-general` },
        ] : []),
    ]

    return (
        <div className='container'>
            <h3>{label} Agreements</h3>

            <Row>
                <Col xs={12} lg={6}>
                    <Panel bsStyle='success'>
                        <Panel.Heading>Streamlined Documents, Purchase Orders</Panel.Heading>
                        <Panel.Body>
                            <FormWrapper horizontal>
                                <SectionList
                                    typeList={streamlinedList}
                                    fileList={streamlinedFiles}
                                    onFileDownload={handleFileDownload} />

                                <FormGroup>
                                    <Col xs={12} sm={6} componentClass={ControlLabel}>Purchase Order Number:</Col>
                                    <Col xs={12} sm={6}>
                                        <div className='form-control-static'>
                                            {invoicePoNumber != '' ? invoicePoNumber : '- not specified -'}
                                        </div>
                                    </Col>
                                </FormGroup>

                                <FormGroup>
                                    <Col xs={12} sm={6} componentClass={ControlLabel}>Purchase Order Expiration Date:</Col>
                                    <Col xs={12} sm={6}>
                                        <div className='form-control-static'>
                                            {invoicePoExpirationDate != '' ? invoicePoExpirationDate : '- not specified -'}
                                        </div>
                                    </Col>
                                </FormGroup>
                            </FormWrapper>
                        </Panel.Body>

                    </Panel>
                </Col>

                {props.app.site.organType == OrganType.Kidney && (
                    <Col xs={12} lg={6}>
                        <Panel bsStyle='warning'>
                            <Panel.Heading>Legacy Documents</Panel.Heading>
                            <Panel.Body>
                                <FormWrapper horizontal>
                                    <SectionList
                                        typeList={legacyList}
                                        fileList={notStreamlinedFiles}
                                        onFileDownload={handleFileDownload} />
                                </FormWrapper>
                            </Panel.Body>
                        </Panel>
                    </Col>
                )}
            </Row>
            <Row>
                <Col xs={12} lg={6}>
                    <Panel bsStyle='primary'>
                        <Panel.Heading>Terms &amp; Conditions, Policies, and Center Requirements</Panel.Heading>
                        <Panel.Body>
                            <div className='text-center'>
                                {linkList.map(item =>
                                    <div>
                                        <a href={item.href} target='_blank'>{item.label}</a>
                                    </div>
                                )}
                            </div>
                        </Panel.Body>
                    </Panel>
                </Col>
            </Row>
        </div>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getData: (centerId) => {
            return dispatch(CenterActionCreators.getLegalDocuments(centerId));
        },
        getVersionedFile: (centerId, id) => {
            return dispatch(CenterActionCreators.getVersionedFile(centerId, id));
        },
    }
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(CenterLegalDocuments);
